import { useIntersectionObserver } from '@/hooks/use-intersection-observer';
import { RefCallback, useEffect, useRef, useState } from 'react';

interface UseClientPaginationOptions {
    /** How many items to render on first paint. Defaults to 24. */
    initialCount?: number;
    /** How many additional items to reveal each time the sentinel intersects. Defaults to 24. */
    batchSize?: number;
    /**
     * Value that re-seeds pagination when it changes. Defaults to
     * `items.length`. Pass a content-derived key (e.g. joined ids) when the
     * list can change without its length changing, so the reseed stays in sync
     * with the consumer's own reset logic (`nonNullChildren` in
     * `RecentContent`/`FavoritesContent`). Use a primitive (`string | number`),
     * not an object/array — the reseed compares it with `!==`.
     */
    resetKey?: string | number;
}

interface UseClientPaginationResult<T> {
    /** The leading slice of `items` that should currently be rendered. */
    visibleItems: T[];
    /** How many items are currently revealed. */
    visibleCount: number;
    /** Whether there are more items left to reveal. */
    hasMore: boolean;
    /** Whether a batch is being revealed: the sentinel is in view and more items remain. */
    isRevealing: boolean;
    /**
     * Attach to a sentinel element rendered after the visible items. When it
     * scrolls into view the next batch is revealed. Only render the sentinel
     * while `hasMore` is true.
     */
    sentinelRef: RefCallback<HTMLDivElement>;
}

/**
 * Paginates a large, already-in-memory list on the client. Renders an initial
 * batch and reveals subsequent batches when a sentinel element (attached via
 * `sentinelRef`) scrolls into view, so expensive per-item work — DOM nodes,
 * IntersectionObservers, per-card API calls — only happens for revealed items.
 *
 * Built on `useIntersectionObserver`, mirroring the "scrolled to bottom"
 * sentinel pattern in `dynamic-home`.
 */
export function useClientPagination<T>(
    items: T[],
    {
        initialCount = 24,
        batchSize = 24,
        resetKey,
    }: UseClientPaginationOptions = {}
): UseClientPaginationResult<T> {
    const [visibleCount, setVisibleCount] = useState(() =>
        Math.min(initialCount, items.length)
    );

    // Whether the sentinel is currently within the observer's root margin. The
    // IntersectionObserver only fires on the enter/leave *edges*, so this state
    // holds "still in view" between them and drives the reveal effect below.
    // Kept as state (not a ref) so a flip re-runs that effect.
    const [isSentinelInView, setIsSentinelInView] = useState(false);

    // Re-seed when the list changes: a cold load (the query resolves on a later
    // render so `items` arrives non-empty later), navigation to a different
    // list, or a shrink. Keyed on `resetKey` when provided (content-derived),
    // else `items.length`. We *clamp* rather than hard-reset so a background
    // *grow* — a react-query refetch, or the user favoriting something
    // elsewhere — preserves an already-scrolled window instead of collapsing it
    // back to `initialCount` and yanking the user up to the first batch.
    // Adjust-state-during-render pattern, as used by `RecentContent` for
    // `nonNullChildren`.
    const resetSignal = resetKey ?? items.length;
    const prevResetSignalRef = useRef(resetSignal);
    if (prevResetSignalRef.current !== resetSignal) {
        prevResetSignalRef.current = resetSignal;
        setVisibleCount((count) =>
            Math.min(Math.max(count, initialCount), items.length)
        );
    }

    const hasMore = visibleCount < items.length;

    // Latest length in a ref so the (stable) reveal callback clamps against the
    // current list without re-subscribing the observer.
    const itemsLengthRef = useRef(items.length);
    itemsLengthRef.current = items.length;

    const sentinelRef = useIntersectionObserver({
        observerOptions: { rootMargin: '600px' },
        intersectingCallback: () => setIsSentinelInView(true),
        nonIntersectingCallback: () => setIsSentinelInView(false),
    });

    // An unobserved sentinel never reports the exit, so clear it here.
    if (!hasMore && isSentinelInView) {
        setIsSentinelInView(false);
    }

    // Reveal one batch per animation frame while the sentinel stays within the
    // margin and there's more to show.
    //
    // The observer only re-fires when the sentinel crosses the margin edge, so
    // we can't lean on it to keep paginating: on a wide/large screen a batch of
    // cards spans only a couple of rows and never pushes the sentinel out of
    // the 600px margin, so `intersectingCallback` fires once and then never
    // again — pagination would stall with the loader stuck on screen. Instead,
    // once the sentinel is in view we self-drive: reveal a batch, then on the
    // next frame (after the browser has laid out the new cards and the observer
    // has had a chance to recompute) reveal again if it's still in view. When
    // enough batches finally push the sentinel past the margin the observer
    // fires `nonIntersectingCallback`, `isSentinelInView` flips false, and the
    // loop stops. Pacing at one batch per frame — rather than revealing
    // synchronously inside the observer callback, where repeated intersecting
    // fires from layout shifts would stack — avoids mounting a flood of cards.
    useEffect(() => {
        if (!isSentinelInView || !hasMore) {
            return;
        }
        const frame = requestAnimationFrame(() => {
            setVisibleCount((count) =>
                Math.min(count + batchSize, itemsLengthRef.current)
            );
        });
        return () => cancelAnimationFrame(frame);
    }, [isSentinelInView, visibleCount, hasMore, batchSize]);

    return {
        visibleItems: items.slice(0, visibleCount),
        visibleCount,
        hasMore,
        isRevealing: isSentinelInView && hasMore,
        sentinelRef,
    };
}

export default useClientPagination;
